<script setup>
import { defineAsyncComponent, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useUserStore } from '@/stores/user'
import { useCustomerStore } from '@/stores/customer'
import { useDemoLogic } from '@/use/demoLogic'
import { api } from '@/plugins/axios'
import { sentryUser } from '@/plugins/sentry'
import event from '@/plugins/events'

const AppBar = defineAsyncComponent(() => import('@/components/Shared/AppBar.vue'))
const AppNavigationDrawer = defineAsyncComponent(() => import('@/components/Shared/AppNavigationDrawer.vue'))
const CustomerSupportCta = defineAsyncComponent(() => import('@/components/Shared/ContactUsCTA.vue'))
const Alert = defineAsyncComponent(() => import('@/components/Shared/Alert.vue'))
const AppFooter = defineAsyncComponent(() => import('@/components/Shared/AppFooter.vue'))
const DemoNotice = defineAsyncComponent(() => import('@/components/Shared/DemoNotice.vue'))

const {mobile, name} = useDisplay()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const openDrawer = ref(false)

const refreshAuth = async () => {
  // console.log(`LOGGED IN: ${userStore.loggedIn}`)
  if (userStore.loggedIn) {
    try {
      const { token } = await api.post('/login/refresh')
      console.log(`Assigning new auth token`)
      api.defaults.headers = { Authorization: `Bearer ${token}` }
      userStore.setToken(token)
      console.log('Auth Refreshed')
    } catch (e) {
      console.error(`REFRESH AUTH ERROR:`, e)
      logout()
    }
  }
}

// LOGOUT
const logoutLoading = ref(false)
const logout = async (reason) => {
  logoutLoading.value = true
  if (typeof reason != 'string') reason = null
  try {
    await api.delete('/login')
  } catch (e) {
    console.error(e)
  }
  userStore.logout()
  useCustomerStore().logout()
  sentryUser()
  router.push({name: 'login', query: route.query || {}})
  logoutLoading.value = false
}

// CONTACT US
const showContactUs = ref(false)
const openContactUs = () => showContactUs.value = true

// FOOTER
const showFooter = computed(() => route.name == 'login')
const env = import.meta.env.VITE_ENV

// DEMO
const {isDemoMode} = useDemoLogic()
const showDemoNotice = ref(false)

onMounted(async () => {
  if (userStore.loggedIn) {
    const user = userStore.getUser
    sentryUser({
      id: user.user_id,
      username: `${user.first_name} ${user.last_name}`,
      email: user.email
    })
    await refreshAuth()
  }
  event.addListener('logout', logout)
  event.addListener('showContactUs', openContactUs)
  if (isDemoMode) showDemoNotice.value = true
})
</script>

<template lang="pug">
v-app#app
  app-bar(@open="openDrawer = !openDrawer")
  app-navigation-drawer(v-if="mobile", :open="openDrawer", @close="openDrawer = false")
  v-main
    router-view
    app-footer(v-if="showFooter")
  
  .text-caption.bottom-right(v-if="env == 'development'", style="z-index: 99999;") {{ name }} - {{ mobile }}

  alert

  v-dialog(v-model="showDemoNotice", persistent)
    demo-notice.mx-auto(@close="showDemoNotice = false")

  v-dialog(v-model="showContactUs", persistent)
    customer-support-cta.mx-auto(@close="showContactUs = false")

  v-overlay.justify-center.align-center(v-model="logoutLoading")
    v-progress-circular(color="primary", indeterminate, size="64")
</template>

<style scoped>
.bottom-right {
  position: fixed;
  right: 2px;
  bottom: 0px;
}
</style>
